/**
 * Company Dashboard Page
 * Assembles all dashboard components with data from useDashboard hook.
 * Protected route requiring at least AGENT role.
 */

import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { useDashboard } from '../../hooks/useDashboard';
import PageHeader from '../../components/common/PageHeader';
import { DashboardHeader } from '../../components/dashboard/DashboardHeader';
import { KPIRow } from '../../components/dashboard/KPIRow';
import { QuickActions } from '../../components/dashboard/QuickActions';
import { AgentBreakdown } from '../../components/dashboard/AgentBreakdown';
import { ActivityFeed } from '../../components/dashboard/ActivityFeed';
import { DashboardTips } from '../../components/dashboard/DashboardTips';

/**
 * Loading skeleton for dashboard page.
 */
function DashboardSkeleton() {
  return (
    <div className="animate-pulse space-y-6">
      {/* Header skeleton */}
      <div className="h-40 rounded-2xl bg-gray-200" />
      {/* KPI row skeleton */}
      <div className="grid grid-cols-3 gap-3">
        <div className="h-24 rounded-xl bg-gray-200" />
        <div className="h-24 rounded-xl bg-gray-200" />
        <div className="h-24 rounded-xl bg-gray-200" />
      </div>
      {/* Quick actions skeleton */}
      <div className="grid grid-cols-3 gap-3">
        <div className="h-20 rounded-xl bg-gray-200" />
        <div className="h-20 rounded-xl bg-gray-200" />
        <div className="h-20 rounded-xl bg-gray-200" />
      </div>
      {/* Agent breakdown skeleton */}
      <div className="space-y-2">
        <div className="h-6 w-32 rounded bg-gray-200" />
        <div className="h-16 rounded-xl bg-gray-200" />
        <div className="h-16 rounded-xl bg-gray-200" />
      </div>
      {/* Activity feed skeleton */}
      <div className="space-y-2">
        <div className="h-6 w-32 rounded bg-gray-200" />
        <div className="h-32 rounded-xl bg-gray-200" />
      </div>
    </div>
  );
}

/**
 * Error state component for dashboard page.
 */
function DashboardError({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  const { t } = useTranslation('dashboard');

  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
        <svg
          className="h-8 w-8 text-red-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      </div>
      <h3 className="mb-2 text-lg font-medium text-gray-900">{t('error')}</h3>
      <p className="mb-4 max-w-md text-center text-gray-600">{message}</p>
      <button
        onClick={onRetry}
        className="rounded-lg bg-[#004D40] px-4 py-2 text-white transition hover:bg-[#003930]"
      >
        Retry
      </button>
    </div>
  );
}

export default function DashboardPage() {
  const { t, i18n } = useTranslation('dashboard');
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useDashboard();

  // Navigation callbacks
  const handleViewProperties = () => navigate('/listings');
  const handleManageTeam = () => navigate('/company/team');
  const handleSettings = () => navigate('/company/settings'); // Will 404 until Phase 4

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white" dir={i18n.dir()}>
        <div className="mx-auto max-w-4xl px-4 py-6">
          <DashboardSkeleton />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-white" dir={i18n.dir()}>
        <div className="mx-auto max-w-4xl px-4 py-6">
          <DashboardError message={error} onRetry={refetch} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-8" dir={i18n.dir()}>
      <PageHeader
        title={t('title', 'Company Dashboard')}
        backTo="/"
        backLabel={t('backToHome', 'Home')}
        showHome={false}
      />
      <div className="mx-auto max-w-4xl space-y-6 px-4 py-6">
        <DashboardHeader
          companyName={data?.companyName || 'Your Company'}
          logoUrl={data?.companyLogoUrl}
          totalListings={data?.totalListings || 0}
          maxListings={data?.maxListings || 50}
          usagePercent={data?.usagePercent || 0}
          totalMembers={data?.totalMembers || 0}
          maxMembers={data?.maxMembers || 2}
          hasSubscription={data?.hasSubscription || false}
        />

        <KPIRow
          activeListings={data?.activeListings || 0}
          pendingListings={data?.pendingListings || 0}
          totalAgents={data?.totalAgents || 0}
          onAgentsClick={handleManageTeam}
        />

        <QuickActions
          onViewProperties={handleViewProperties}
          onManageTeam={handleManageTeam}
          onSettings={handleSettings}
        />

        {/* Statistics page link — broker-level performance breakdown across
            the company. The page itself enforces COMPANY_ADMIN role; the
            link is shown to everyone here and gracefully 403s if accessed
            by non-admins. */}
        <Link
          to="/company/statistics"
          className="flex items-center justify-between gap-3 rounded-2xl border border-[#E0E9E7] bg-white px-5 py-4 shadow-sm transition hover:border-[#3C9F8F] hover:shadow-md"
        >
          <div className="flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#004D40] text-white">
              <i className="ri-bar-chart-2-line text-lg" aria-hidden />
            </span>
            <div>
              <div className="font-medium text-gray-900">
                {i18n.language === 'ar' ? 'إحصائيات الشركة' : 'Company Statistics'}
              </div>
              <div className="text-xs text-gray-500">
                {i18n.language === 'ar'
                  ? 'مشاهدات، نقرات واتساب واتصالات لكل وسيط'
                  : 'Views, WhatsApp clicks, and phone clicks per broker'}
              </div>
            </div>
          </div>
          <i className={`ri-arrow-${i18n.dir() === 'rtl' ? 'left' : 'right'}-s-line text-xl text-gray-400`} aria-hidden />
        </Link>

        <AgentBreakdown agents={data?.listingsByAgent || []} />

        <ActivityFeed activities={data?.recentActivity || []} />
      </div>
      <DashboardTips accountType="company" />
    </div>
  );
}
